import { api, unwrapData } from '../../../app/store/api';
import { PaginatedResponse } from '../../../types';
import {
    MarketingWithdrawItem,
    MarketingWithdrawRequestPayload,
    MarketingWithdrawableSummary,
    MarketingWithdrawTransaction,
} from '../types/marketing-withdraw.types';
import { Partner } from '../../partners/pages/partners.page';
import type { MarketingTransaction } from '../types/marketing-transaction.types';
import { MarketingSubset } from '../types/marketing-subset.types';

export const marketingApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getMarketingStatus: builder.query<any, void>({
            transformResponse: unwrapData,
            query: () => ({
                url: 'user/marketing/status',
                method: 'GET',
            }),
            providesTags: ['MarketingStatus'],
        }),

        getMarektingSubset: builder.query<
            PaginatedResponse<MarketingSubset>,
            { page?: number; perPage?: number; search?: any }
        >({
            transformResponse: unwrapData,
            query: ({ page, perPage = 15, search }) => ({
                url: `user/marketing/subset-list`,
                method: 'GET',
                params: {
                    ...(page && { page }),
                    ...(perPage && { per_page: perPage }),
                    ...(search && {
                        ...search,
                    }),
                },
            }),
        }),

        getMarektingSubsetTransaction: builder.query<
            PaginatedResponse<MarketingTransaction>,
            { page?: number; perPage?: number; search: object }
        >({
            transformResponse: unwrapData,
            query: ({ page, perPage = 15, search }) => ({
                url: `user/marketing/subset-transactions`,
                method: 'GET',
                params: {
                    ...(page && { page }),
                    ...(perPage && { per_page: perPage }),
                    ...(search && {
                        ...search,
                    }),
                },
            }),
        }),

        sendInvitationEmail: builder.mutation<unknown, string>({
            transformResponse: unwrapData,
            query: (body) => ({
                url: 'user/marketing/send-email',
                method: 'POST',
                body: { emailAddress: body },
            }),
            invalidatesTags: (result) =>
                result ? [{ type: 'Marketing' }] : [],
        }),

        getEmails: builder.query<any, { page?: number; perPage?: number }>({
            transformResponse: unwrapData,
            query: ({ page, perPage = 15 }) => ({
                url: `user/marketing/show-emails`,
                method: 'GET',
                params: {
                    ...(page && { page }),
                    ...(perPage && { per_page: perPage }),
                },
            }),
            providesTags: ['Marketing'],
        }),

        getIncomeChart: builder.query<any, { period: ChartPeriod }>({
            transformResponse: unwrapData,
            query: ({ period }) => {
                const params =
                    typeof period === 'string'
                        ? { period }
                        : {
                              start_date: period[0],
                              end_date: period[1],
                          };

                return {
                    url: 'user/marketing/value-charts',
                    method: 'GET',
                    params,
                };
            },
        }),

        getSubUserChart: builder.query<any, { period: ChartPeriod }>({
            transformResponse: unwrapData,
            query: ({ period }) => {
                const params =
                    typeof period === 'string'
                        ? { period }
                        : {
                              start_date: period[0],
                              end_date: period[1],
                          };

                return {
                    url: 'user/marketing/sub-user-charts',
                    method: 'GET',
                    params,
                };
            },
        }),

        inviteAction: builder.mutation<any, string>({
            transformResponse: unwrapData,
            query: (body) => ({
                url: 'user/referral-code/check',
                method: 'POST',
                body: { code: body },
            }),
        }),


        marketingWithdrawableSummary: builder.query<MarketingWithdrawableSummary, void>({
            transformResponse: unwrapData,
            query: () => ({
                url: 'user/marketing/withdraw/summary',
                method: 'GET',
            }),
            providesTags: ['MarketingWithdraw'],
        }),

        requestMarketingWithdraw: builder.mutation<any, MarketingWithdrawRequestPayload>({
            transformResponse: unwrapData,
            query: (body) => ({
                url: 'user/marketing/withdraw',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result) =>
                result
                    ? [{ type: 'MarketingWithdraw' }, { type: 'MarketingStatus' }]
                    : [],
        }),

        withdrawList: builder.query<
            PaginatedResponse<MarketingWithdrawItem>,
            { page?: number; perPage?: number }
        >({
            transformResponse: unwrapData,
            query: ({ page, perPage = 15 }) => ({
                url: 'user/marketing/withdraw',
                method: 'GET',
                params: {
                    ...(page && { page }),
                    ...(perPage && { per_page: perPage }),
                },
            }),
            providesTags: ['MarketingWithdraw'],
        }),

        marketingWithdrawDetails: builder.query<MarketingWithdrawItem, number>({
            transformResponse: unwrapData,
            query: (id) => ({
                url: `user/marketing/withdraw/${id}`,
                method: 'GET',
            }),
            providesTags: ['MarketingWithdraw'],
        }),

        marketingWithdrawTransactions: builder.query<
            PaginatedResponse<MarketingWithdrawTransaction>,
            { id: number; page?: number; perPage?: number }
        >({
            transformResponse: unwrapData,
            query: ({ id, page, perPage = 15 }) => ({
                url: `user/marketing/withdraw/${id}/transactions`,
                method: 'GET',
                params: {
                    ...(page && { page }),
                    ...(perPage && { per_page: perPage }),
                },
            }),
            providesTags: ['MarketingWithdraw'],
        }),

        withdrawDetails: builder.query<any, string>({
            transformResponse: unwrapData,
            query: (token) => ({
                url: `marketing-withdrawals/receipts/${token}`,
                method: 'GET',
            }),
        }),

        partnerList: builder.query<
            PaginatedResponse<Partner>,
            { page?: number; perPage?: number }
        >({
            transformResponse: unwrapData,
            query: ({ page = 1, perPage = 15 }) => ({
                url: 'partners',
                method: 'GET',
                params: {
                    ...(page && { page }),
                    ...(perPage && { per_page: perPage }),
                },
            }),
        }),
    }),
});

export const {
    useGetMarketingStatusQuery,
    useGetMarektingSubsetQuery,
    useSendInvitationEmailMutation,
    useInviteActionMutation,
    useGetEmailsQuery,
    useWithdrawListQuery,
    useMarketingWithdrawableSummaryQuery,
    useMarketingWithdrawDetailsQuery,
    useMarketingWithdrawTransactionsQuery,
    useWithdrawDetailsQuery,
    useRequestMarketingWithdrawMutation,
    useGetMarektingSubsetTransactionQuery,
    usePartnerListQuery,
    useGetSubUserChartQuery,
    useGetIncomeChartQuery,
} = marketingApi;

export default marketingApi;

export type DateRangeType = {
    startDate: string;
    endDate: string;
};

export type ChartPeriod =
    | 'daily'
    | 'weekly'
    | 'monthly'
    | 'threeMonths'
    | 'sixMonths'
    | 'yearly'
    | 'custom'
    | string[];
